import React, { useEffect, useRef, useState } from "react";

// The three filters that answer "what is still open, for which year end": a
// Status checklist (every status but Completed ticked by default), the fiscal
// year, and the year-end month. They sit in the toolbar rather than behind
// the Filter panel because they are the everyday cut of the grid.
//
// Status keys match taskFilters.statusKeyOf: a status id as a string, "none"
// for a task with no status, "completed" for every finished task. The
// "Completed" statuses themselves are folded into that one checkbox.

export const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export const DEFAULT_HIDDEN_STATUSES = ["completed"];

const isCompletedName = (s) => (s.name || "").trim().toLowerCase() === "completed";

export default function TaskFilterBar({
  statuses = [], hiddenStatuses, onHiddenStatusesChange,
  fiscalYear, fiscalYears = [], onFiscalYearChange,
  yearEndMonth, onYearEndMonthChange,
  nextYear = "", onNextYearChange,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const options = [
    { key: "none", label: "(No status)" },
    ...statuses.filter((s) => !isCompletedName(s))
      .map((s) => ({ key: String(s.id), label: s.name })),
    { key: "completed", label: "Completed" },
  ];

  const toggle = (key) => {
    const next = new Set(hiddenStatuses);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onHiddenStatusesChange(next);
  };

  const isDefault =
    hiddenStatuses.size === DEFAULT_HIDDEN_STATUSES.length &&
    DEFAULT_HIDDEN_STATUSES.every((k) => hiddenStatuses.has(k));
  const summary = hiddenStatuses.size === 0
    ? "All"
    : isDefault
      ? "Open"
      : `${options.length - hiddenStatuses.size} of ${options.length}`;

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <div ref={ref} style={{ position: "relative" }}>
        <button
          type="button"
          aria-haspopup="true"
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          style={{ ...control, cursor: "pointer",
                   color: isDefault ? "#323338" : "#0073ea" }}
        >
          Status: {summary} ▾
        </button>
        {open && (
          <div role="group" aria-label="Status filter" style={menu}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          padding: "2px 6px 6px", borderBottom: "1px solid #f3f4f6" }}>
              <button type="button" style={link}
                      onClick={() => onHiddenStatusesChange(new Set())}>
                All
              </button>
              <button type="button" style={link}
                      onClick={() => onHiddenStatusesChange(new Set(DEFAULT_HIDDEN_STATUSES))}>
                Open only
              </button>
            </div>
            {options.map((o) => (
              <label key={o.key} style={item}>
                <input type="checkbox" checked={!hiddenStatuses.has(o.key)}
                       onChange={() => toggle(o.key)} />
                {o.label}
              </label>
            ))}
          </div>
        )}
      </div>

      <select aria-label="Fiscal year" value={fiscalYear}
              onChange={(e) => onFiscalYearChange(e.target.value)}
              title="The chosen year in full. Plus anything unfinished from earlier years"
              style={control}>
        <option value="">All years</option>
        {fiscalYears.map((y) => <option key={y} value={y}>FY {y}</option>)}
      </select>

      <select aria-label="Year-end month" value={yearEndMonth}
              onChange={(e) => onYearEndMonthChange(e.target.value)}
              style={control}>
        <option value="">Any year-end month</option>
        {MONTHS.map((m, i) => <option key={m} value={String(i + 1)}>{m}</option>)}
      </select>

      {onNextYearChange && (
        <select aria-label="Next year" value={nextYear}
                onChange={(e) => onNextYearChange(e.target.value)}
                title="What became of each finished file the following year"
                style={{ ...control, color: nextYear ? "#0073ea" : undefined }}>
          <option value="">Next year: any</option>
          <option value="waiting">Waiting to roll</option>
          <option value="rolled">Rolled forward</option>
          <option value="stopped">Won't roll</option>
        </select>
      )}
    </div>
  );
}

const control = {
  padding: "4px 8px", fontSize: 13, border: "1px solid #d0d4e4",
  borderRadius: 4, background: "#fff", outline: "none",
};
const menu = {
  position: "absolute", top: "calc(100% + 4px)", left: 0, background: "white",
  border: "1px solid #d0d4e4", borderRadius: 6,
  boxShadow: "0 4px 12px rgba(0,0,0,0.12)", minWidth: 200, maxHeight: 320,
  overflowY: "auto", zIndex: 200, padding: 6,
};
const item = {
  display: "flex", alignItems: "center", gap: 8, padding: "5px 6px",
  fontSize: 13, cursor: "pointer",
};
const link = {
  fontSize: 11, color: "#0073ea", background: "transparent", border: "none",
  cursor: "pointer", padding: 0,
};
